import { createSelector } from '@reduxjs/toolkit';
import { State } from 'app/store';

import { isAwaitingPayment, isCompletedPayment } from '../lib';

import {
  PaymentCompleteState,
  PaymentInsufficientState,
  PaymentMintingState,
  PaymentPendingState,
} from './payment.types';

export const selectPaymentState = (state: State) => state.payment;
export const selectCurrentPayment = (state: State) => state.payment.payment;

export const selectAwaitingPayment = (
  state: State,
): PaymentPendingState | PaymentInsufficientState | PaymentMintingState | null => {
  const { payment } = state.payment;
  return payment && isAwaitingPayment(payment) ? payment : null;
};

export const selectCompletedPayment = (state: State): PaymentCompleteState | null => {
  const { payment } = state.payment;
  return payment && isCompletedPayment(payment) ? payment : null;
};

export const selectPaymentAssets = (state: State) => state.payment.paymentAssets;
export const selectAllPaymentMethods = createSelector([selectPaymentAssets], (assetsEntities) => {
  const { tokens, networks } = assetsEntities;

  return tokens.allIds.map((tokenId) => {
    const token = tokens.byId[tokenId];

    const network = networks.byId[token.networkId];

    return {
      tokenId,
      tokenName: token.name,
      tokenSymbol: token.symbol,
      tokenImage: token.image,
      tokenAddress: token.address,
      networkName: network.name,
      networkImage: network.image,
    };
  });
});

export const selectEstimatedPayment = (state: State) => state.payment.paymentEstimate;

export const selectCurrentTokenId = (state: State) => state.payment.paymentTokenId;
export const selectCurrentPaymentAsset = createSelector(
  [selectPaymentAssets, selectCurrentTokenId],
  (assetsEntities, currentTokenId) => {
    const { tokens, networks } = assetsEntities;

    if (!currentTokenId) return null;

    const token = tokens.byId[currentTokenId];

    const network = networks.byId[token.networkId];

    return {
      ...token,
      tokenNetworkName: network.name,
      tokenNetworkShortName: network.shortName,
      tokenNetworkImage: network.image,
      tokenId: currentTokenId,
    };
  },
);

export const selectUserRefCode = (state: State) => state.payment.paymentHdWallets[0]?.referralCode;
